import React, { useState, useEffect } from 'react';
// Common Components (Corrected Paths)
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API & Utils
import { getVouchers, destroyVoucher } from '../../../api/VouchersAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Helpers
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns';

const VouchersPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Voucher');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);

    const [vouchersList, setVouchersList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false); // Triggers refresh

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/finance/vouchers/create');
    const handleEditClick = (id) => navigate(`/franchisee_user/finance/vouchers/edit/${id}`);

    // --- Delete Logic (Now with frontend check) ---
    const handleDeleteClick = (voucher) => {
        
        // ** FIX: Check the new `system_voucher_details_count` attribute **
        if (voucher.system_voucher_details_count > 0) {
            Swal.fire(
                'Action Not Allowed',
                'This is a system-generated voucher (e.g., from a Sales Invoice) and cannot be deleted from this form.',
                'warning'
            );
            return;
        }

        Swal.fire({
            title: 'Are you sure?',
            text: `This will permanently delete Voucher #${voucher.voucher_number}. You won't be able to revert this!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyVoucher(voucher.id, (response) => {
                    if (response.success) {
                        setToaster({ type: 'success', heading: 'Success', message: `Voucher #${voucher.voucher_number} deleted.` });
                        setShowToaster(true);
                        setSaveCompleted(prev => !prev); 
                    } else {
                        handleApiError(response, () => {});
                        setToaster({ type: 'danger', heading: 'Error', message: response.error?.response?.data?.message || 'Could not delete voucher.' });
                        setShowToaster(true);
                    }
                });
            }
        });
    };

    // --- Effects ---
    useEffect(() => {
         if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
         }
     }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
         document.title = 'Manage Vouchers';
         let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
         setViewDataLoading(true);
         setSaveCompleted(false);

         getVouchers(filters, function (response) {
             setViewDataLoading(false);
             if (response.vouchers) { 
                 let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                 if (Array.isArray(response.vouchers)) { data = response.vouchers; }
                 else if (response.vouchers.data && Array.isArray(response.vouchers.data)) {
                     data = response.vouchers.data; currentP = response.vouchers.current_page; lastP = response.vouchers.last_page; responsePP = response.vouchers.per_page;
                 } else { console.error("API structure error:", response.vouchers); handleApiError(response); return; }
                 setVouchersList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
             } else {
                  handleApiError(response); setToaster({ type: 'danger', heading: 'Error', message: 'Could not load vouchers.' }); setShowToaster(true);
                  setVouchersList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
             }
         });
     }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    // --- Helpers ---
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try { return format(parseISO(dateString), 'dd-MMM-yyyy'); }
        catch (error) { return dateString; }
    };
    
    const getTotalAmount = (voucher) => {
        return parseFloat(voucher.total_amount || 0).toFixed(2);
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Vouchers List'>
                        <li><a className="f-s-14 f-w-500" href="#">Finance</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Vouchers</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Vouchers</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add Manual Voucher
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {vouchersList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={vouchersList.current_page} totalPages={vouchersList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No vouchers found.</p> )}
                                    {(viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && vouchersList.data.map((voucher) => {
                                            // ** FIX: Check the new attribute name **
                                            const isSystemVoucher = voucher.system_voucher_details_count > 0;
                                         
                                            return (
                                                <div className="col-md-6 col-xl-4 project-card" key={voucher.id}>
                                                    <div className="card hover-effect">
                                                        <div className="card-header">
                                                            <div className="d-flex align-items-center">
                                                                <div className={`h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit ${isSystemVoucher ? 'text-muted' : 'text-secondary'}`}>
                                                                    <i className={`ti ${isSystemVoucher ? 'ti-book' : 'ti-manual-gearbox'} f-s-18`}></i>
                                                                </div>
                                                                <div className="flex-grow-1 ps-2">
                                                                    <h6 className="m-0 text-dark f-w-600">{voucher.voucher_type?.name || 'Voucher'} - {voucher.party?.name || 'N/A'}</h6>
                                                                    <small className="text-muted d-block">No: {voucher.voucher_number || '-'} | {formatDate(voucher.voucher_date)}</small>
                                                                </div>
                                                                <div className="dropdown">
                                                                    <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                                        <li><a className="dropdown-item" onClick={() => handleEditClick(voucher.id)}><i className="ti ti-eye text-info me-1" /> View / Edit</a></li>
                                                                        <li><button 
                                                                                className="dropdown-item delete-button" 
                                                                                onClick={() => handleDeleteClick(voucher)} 
                                                                                disabled={isSystemVoucher}
                                                                            >
                                                                            <i className="ti ti-trash text-danger me-1" /> Delete
                                                                        </button></li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="card-body py-2">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <p className="f-w-500 text-secondary mb-0">Total Amount</p>
                                                                    <h6 className="f-w-600 mb-0">₹{getTotalAmount(voucher)}</h6>
                                                                </div>
                                                                <div className='text-end'>
                                                                    <p className="f-w-500 text-secondary mb-0">Narration</p>
                                                                    <h6 className="f-w-600 mb-0 text-truncate" style={{maxWidth: '150px'}}>{voucher.narration || '-'}</h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {!viewDataLoading && vouchersList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Vouchers Found</h5><p className="card-text">No records created yet. You can create manual vouchers using the 'Add Manual Voucher' button.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>

                                
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default VouchersPage;